import React, { useState } from 'react';
import Sheet from '@mui/joy/Sheet';
import FilesModal from './FilesModal';
import CssBaseline from '@mui/joy/CssBaseline';
import IconButton from '@mui/material/IconButton';
import TopicIcon from '@mui/icons-material/Topic';
import CheckIcon from '@mui/icons-material/Check';
import AddQuotationRequest from './AddQuotationRequest';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import StatusBadge from '../../../../../../../common/StatusBadge';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ToastMessage from '../../../../../../../common/ToastMessage';
import useQuotationRequests from '../../../../../../../store/ReactQueries/procurements/quotationRequests/useQuotationRequests';
import useDeleteQuotationRequest from '../../../../../../../store/ReactQueries/procurements/quotationRequests/useDeleteQuotationRequest';
import useApproveQuotationRequest from '../../../../../../../store/ReactQueries/procurements/quotationRequests/useApproveQuotationRequest';
import useApprovalRequestByPurchaseOrderId from '../../../../../../../store/ReactQueries/procurements/approvalRequests/useApprovalRequestByPurchaseOrderId';
import { Button } from '@mui/material';
import { ModeTypes } from '../AddPurchaseOrder';
import { useUserStore } from '../../../../../../../store/useUserStore';
import { formatNumber } from '../../../../../../../common/GlobalFunctions';
import { useDarkModeStore } from '../../../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../../../store/useLanguageStore';
import { QuotationRequestInterface } from '../../../../../../../models/ProcurementModule/QuotationRequest';

interface QuotationsProps {
    mode: ModeTypes;
    isApproved: boolean;
    purchaseOrderId: string | undefined
}

export const QuotationRequests: React.FC<QuotationsProps> = ({ mode, isApproved, purchaseOrderId }) => {

    const { user } = useUserStore();
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();

    const initialValueQuotation: QuotationRequestInterface = {
        id: `New-Quotation-Req-${Math.random()}`,
        purchaseOrderId: purchaseOrderId ?? '',
        price: 0,
        uploadFiles: [],
        files: [],
        vendor: { name: '' },
        vendorId: '',
    }

    const { data: quotationRequestsData } = useQuotationRequests(purchaseOrderId, 0, 0);
    const { data: approvalPersonsById } = useApprovalRequestByPurchaseOrderId(purchaseOrderId);
    const [quotationForm, setQuotationForm] = useState<QuotationRequestInterface>(initialValueQuotation);
    const deleteQuotationRequest = useDeleteQuotationRequest(purchaseOrderId, 0, 0)
    const addApproveRequest = useApproveQuotationRequest(purchaseOrderId, 0, 0);

    const [quotationModal, setQuotationModal] = useState<boolean>(false);
    const [modal, setModal] = useState<{ id: string, value: boolean }>({ id: '', value: false });

    const hasApprovedOffer = quotationRequestsData?.some(item => item.isApproved);

    const handleRemoveRow = async (id: string) => {
        if (await ToastMessage.Confirm()) {
            deleteQuotationRequest.mutate(id);
        }
    };

    const handleModal = (id: string) => setModal({ ...modal, id, value: true });
    const handleQuotationModal = () => setQuotationModal(!quotationModal);

    const handleEdit = (id: string) => {
        const result = quotationRequestsData?.find(item => item.id === id);
        if (result) {
            setModal({ ...modal, id })
            setQuotationForm(result);
            handleQuotationModal();
        }
    }

    const smallestPriceIds = (() => {
        if (!quotationRequestsData.length) return [];
        const minPrice = Math.min(...quotationRequestsData.map(obj => obj.price));
        return quotationRequestsData
            .filter(item => item.price === minPrice)
            .map(item => item.id);
    })();

    const handleApproveRequest = async (form: QuotationRequestInterface) => {
        if (await ToastMessage.Confirm(language['ApproveQRequestMessage'])) {
            addApproveRequest.mutate(form);
        }
    }
    //check if current user is at approvalPersons list of this purchase order
    const hasApprovedBtn = approvalPersonsById?.some(item => item.author?.id === user?.userId);

    return (
        <div className="w-100 d-flex justify-content-center">
            <CssBaseline />
            <Sheet
                sx={{
                    my: 0,
                    py: 4,
                    px: 4,
                    gap: 0,
                    width: '100%',
                    display: 'flex',
                    borderRadius: 'md',
                    flexDirection: 'column',
                    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
                    background: darkMode ? 'var(--dark-mode-bg)' : 'white',
                }}
            >
                <div
                    className="mb-0 d-flex flex-row align-items-center"
                    style={{ color: darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)', fontSize: 20 }}
                >
                    <span>{language['RequestForQuotation']}</span>
                    {(!isApproved && !hasApprovedOffer)
                        && <IconButton
                            aria-label="edit"
                            title='Add Product'
                            sx={{ color: '#185EA5', marginLeft: 'auto', marginBottom: 0.5 }}
                            onClick={handleQuotationModal}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                    }
                </div>

                <div className="mt-4">
                    <div style={{ overflowX: 'auto' }}>
                        <table className={`table ${darkMode && 'table-dark'}`}>
                            <thead>
                                <tr>
                                    <th className='text-nowrap' style={{ width: '25%' }}>{language['Vendor']}</th>
                                    <th className='text-nowrap' style={{ width: '10%' }}>{language['Price']}</th>
                                    <th className='text-nowrap' style={{ width: '55%' }}>{language['Comment']}</th>
                                    <th className='text-nowrap' style={{ width: '5%' }} />
                                    <th className='text-nowrap' style={{ width: '5%' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    quotationRequestsData?.map(req =>
                                        <tr
                                            key={`question-request-${req.id}`}
                                        >
                                            <td className='text-nowrap'>
                                                <div className='d-flex flex-row align-items-center gap-3'>
                                                    <span>{req?.vendor?.name}</span>
                                                    {smallestPriceIds.includes(req.id)
                                                        && <StatusBadge name={'Best Offer'} color='#368E71' width={'fit-content'} fontSize={12} />
                                                    }
                                                </div>
                                            </td>
                                            <td className='text-nowrap'>{req.price ? `${formatNumber(req.price)} €` : ''}</td>
                                            <td className='text-nowrap'>{req.comment}</td>
                                            {
                                                hasApprovedOffer
                                                    ? <td className='text-nowrap' style={{ width: '5%' }}>
                                                        {req.isApproved
                                                            && <div className="mt-2">
                                                                <StatusBadge name={'Approved'} color='#368E71' width={'fit-content'} fontSize={12} />
                                                            </div>}
                                                    </td>
                                                    : hasApprovedBtn
                                                        ? <td className='text-nowrap' style={{ width: '5%' }}>
                                                            <Button
                                                                variant='outlined'
                                                                onClick={() => handleApproveRequest(req)}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    color: "var(--btn-color)",
                                                                    borderColor: "var(--btn-color)",
                                                                    '&:hover': {
                                                                        backgroundColor: "#bcbdf8",
                                                                        borderColor: "#bcbdf8",
                                                                    }
                                                                }}
                                                            >
                                                                <CheckIcon sx={{ fontSize: 16, marginRight: .5 }} /> Approve
                                                            </Button>
                                                        </td>
                                                        : <td />
                                            }
                                            <td className='text-nowrap' style={{ width: '5%' }}>
                                                <div className='d-flex flex-row align-items-center gap-1' style={{ width: 'fit-content' }}>
                                                    <IconButton
                                                        aria-label="upload file"
                                                        title="Files"
                                                        sx={{ marginBottom: 0.5, color: 'var(--color-pink)' }}
                                                        component="label"
                                                        onClick={() => handleModal(req.id)}
                                                    >
                                                        <TopicIcon />
                                                    </IconButton>
                                                    {(!isApproved && !hasApprovedOffer)
                                                        && <>
                                                            <IconButton
                                                                aria-label="edit item"
                                                                title="Edit item"
                                                                sx={{ marginBottom: 0.5, color: 'var(--color-pink)', }}
                                                                component="label"
                                                                onClick={() => handleEdit(req.id)}
                                                            >
                                                                <ModeEditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                aria-label="Delete item"
                                                                title='Delete item'
                                                                color='error'
                                                                sx={{ marginBottom: 0.5 }}
                                                                onClick={() => handleRemoveRow(req.id)}
                                                            >
                                                                <DeleteOutlinedIcon />
                                                            </IconButton>
                                                        </>
                                                    }
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                </div>
            </Sheet>
            {
                quotationModal
                && <AddQuotationRequest
                    purchaseOrderId={purchaseOrderId}
                    mode={mode}
                    form={quotationForm}
                    setForm={setQuotationForm}
                    open={modal}
                    handleQuotationModal={handleQuotationModal}
                    onClose={() => setModal({ id: '', value: false })}
                />
            }
            {
                modal
                && <FilesModal
                    open={modal}
                    quotationForm={quotationForm}
                    purchaseOrderId={purchaseOrderId}
                    setQuotationForm={setQuotationForm}
                    hasApprovedOffer={hasApprovedOffer}
                    onClose={() => setModal({ id: '', value: false })}
                />
            }
        </div>
    );
};

export default QuotationRequests;
